import AMapLoader from "@amap/amap-jsapi-loader";
import { Box } from "@mui/material";
import LayerSwitch from "containers/map/layer-switch/layer-switch";
import WorldZoom from "containers/map/world-zoom/world-zoom";
import Zooms from "containers/map/zooms/zooms";
import { useEffect, useRef, useState } from "react";

const amapkey = "aee6edc014f6b2e1f310095a5c40a949";

interface AMapMatchProps {
  readonly center?: [number, number];
  readonly zoom?: number;
  readonly data?: any[];
  readonly selectedId?: string | null;
  readonly onMarkerClick?: (id: string) => void;
}

function AMapMatch({
  center = [113, 30],
  zoom = 3,
  data = [],
  selectedId,
  onMarkerClick
}: AMapMatchProps) {
  const containerIdRef = useRef(
    `map-match-${new Date().getTime()}`
  );
  const mapRef = useRef<any>(null);
  const markersRef = useRef<any[]>([]);
  const AMapRef = useRef<any>(null);
  const prevSelectedIdRef = useRef<string | null>(null);
  const [isMapReady, setIsMapReady] = useState(false);

  useEffect(() => {
    const isUnmounted = { current: false };
    (window as any)._AMapSecurityConfig = {
      securityJsCode: "1ff8751ba0f4086ec444b1acbb56f14a"
    };

    let map: any;
    AMapLoader.load({
      key: amapkey,
      version: "2.0",
      plugins: ["AMap.Marker", "AMap.ToolBar"]
    })
      .then((AMap) => {
        if (isUnmounted.current) {
          return;
        }
        AMapRef.current = AMap;
        map = new AMap.Map(containerIdRef.current, {
          zoom: zoom,
          center: center,
          resizeEnable: true
        });
        mapRef.current = map;

        const scale = new AMap.Scale({
          position: { bottom: "10px", right: "30px" },
        });
        map.addControl(scale);
        // const toolBar = new AMap.ToolBar();
        // map.addControl(toolBar);
        setIsMapReady(true);
      })
      .catch((e) => console.log(e));

    return () => {
      isUnmounted.current = true;
      if (mapRef.current) {
        mapRef.current.destroy();
        mapRef.current = null;
      }
      setIsMapReady(false);
    };
  }, []);

  // Effect for creating/re-creating markers when data changes
  useEffect(() => {
    if (!isMapReady || !mapRef.current || !AMapRef.current) {
      return;
    }
    
    const AMap = AMapRef.current;

    // 清除旧标记
    mapRef.current.remove(markersRef.current);
    markersRef.current = [];

    const markers: any[] = [];
    
    data?.forEach((item) => {
      if (item.longitude && item.latitude) {
        const isSelected = item.id === selectedId;
        const size = isSelected ? { width: 30, height: 42 } : { width: 20, height: 28 };
        const marker = new AMap.Marker({
          position: [item.longitude, item.latitude],
          extData: { id: item.id },
          content: `<img src="/icon_map_pin_green.svg" style="width:${size.width}px;height:${size.height}px;"></img>`,
          offset: new AMap.Pixel(-size.width / 2, -size.height),
          zIndex: isSelected ? 110 : 100
        });
        marker.on("click", (e: any) => {
          if (onMarkerClick) {
            onMarkerClick(e.target.getExtData().id);
          }
        });
        markers.push(marker);
      }
    });

    markersRef.current = markers;

    if (markers.length > 0) {
      mapRef.current.add(markers);
      if (!selectedId) {
        // setFitView(overlays, immediately, avoid, maxZoom)
        // avoid: [top, right, bottom, left]
        mapRef.current.setFitView(undefined, false, [60, 60, 60, 60], 6);
      }
    }
  }, [isMapReady, data, onMarkerClick, selectedId]);

  // Effect for updating marker styles when selectedId changes
  useEffect(() => {
    if (
      !isMapReady ||
      !mapRef.current ||
      !AMapRef.current ||
      markersRef.current.length === 0
    ) {
      return;
    }

    const AMap = AMapRef.current;

    const updateMarkerStyle = (marker: any, isSelected: boolean) => {
      const size = isSelected ? { width: 30, height: 42 } : { width: 20, height: 28 };
      marker.setContent(
        `<img src="/icon_map_pin_green.svg" style="width:${size.width}px;height:${size.height}px;"></img>`
      );
      marker.setOffset(new AMap.Pixel(-size.width / 2, -size.height));
      marker.setzIndex(isSelected ? 110 : 100);
    };

    const prevSelectedMarker = prevSelectedIdRef.current
      ? markersRef.current.find(
          (m) => m.getExtData().id === prevSelectedIdRef.current
        )
      : null;
    if (prevSelectedMarker) {
      updateMarkerStyle(prevSelectedMarker, false);
    }

    const currentSelectedMarker = selectedId
      ? markersRef.current.find((m) => m.getExtData().id === selectedId)
      : null;
    if (currentSelectedMarker) {
      updateMarkerStyle(currentSelectedMarker, true);
      const position = currentSelectedMarker.getPosition();
      if (position) {
        mapRef.current.setCenter(position);
        if (mapRef.current.getZoom() < 7) {
          mapRef.current.setZoom(17);
        }
      }
    }

    prevSelectedIdRef.current = selectedId ?? null;
  }, [isMapReady, selectedId]);

  return (
    <Box style={{ width: "100%", height: "100%", position: "relative" }}>
      <Box
        id={containerIdRef.current}
        style={{ width: "100%", height: "100%", borderRadius: "3px" }}
      />
      <LayerSwitch map={mapRef.current} style={{ right: "30px" }}></LayerSwitch>
      <WorldZoom map={mapRef.current} style={{ right: "30px" }}></WorldZoom>
      <Zooms map={mapRef.current} style={{ right: "30px" }}></Zooms>
    </Box>
  );
}

export default AMapMatch;
